import React, { useEffect, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import Loader from '../../../components/loader';
import { titleCell } from '../../../components/elements/function';
import Button from '../../../components/elements/button';
import moment from 'moment';
import { DATE_FORMAT, PAGE_SIZE } from '../../../_common/constant';
import { useFacilitiesApi } from '../../../store/api/hooks/facilitiesApiHook';
import { useDialog } from '../../../store/api/hooks/dialogHook';
import toast from 'react-hot-toast';
import Pagination from '../../../components/pagination';
import { getPermissionInfo } from '../../../_common/functions';
import { useLocation } from 'react-router-dom';
import { useUserSelector } from '../../../store/selectors/userSelector';
import Icon from '../../../components/icons';

ModuleRegistry.registerModules([AllCommunityModule]);

interface LogActivityProps {
    facilityID?: string | number;
}

const LogActivity: React.FC<LogActivityProps> = ({ facilityID }) => {
    const [logActivityData, setLogActivityData] = useState<any>(null);
    const [gridApi, setGridApi] = useState<any>(null);
    const [gridColumnApi, setGridColumnApi] = useState<any>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const facilityApi = useFacilitiesApi();
    const { confirm } = useDialog();
    const location = useLocation();
    const loggedinuser: any = useUserSelector();

    const columnDefs = [
        {
            headerName: 'Description', 
            field: 'description', 
            headerClass: 'bg-[#f0f0f0]', 
            flex: 1, 
            minWidth: 250,
            cellRenderer: titleCell
        },
        {
            headerName: 'Type', 
            field: 'typeID', 
            headerClass: 'bg-[#f0f0f0]', 
            width: 150,
            cellRenderer: (data: any) => {
                const typeText = data.value === 1 ? 'Observation' : data.value === 2 ? 'Activity' : '-';
                return <p>{typeText}</p>;
            }
        },
        {
            headerName: 'Status', 
            field: 'statusID', 
            headerClass: 'bg-[#f0f0f0]', 
            width: 150,
            cellRenderer: (data: any) => {
                const statusText = data.value === 1 ? 'New' : data.value === 2 ? 'Acknowledged' : '-';
                return <p>{statusText}</p>;
            }
        },
        {
            headerName: 'Facility', 
            field: 'facilityName', 
            headerClass: 'bg-[#f0f0f0]', 
            width: 170,
            cellRenderer: titleCell
        },
        {
            headerName: 'Sub Facility', 
            field: 'subFacilityName', 
            headerClass: 'bg-[#f0f0f0]', 
            width: 150,
            cellRenderer: titleCell
        },
        {
            headerName: 'Activity Date', 
            field: 'activityDate', 
            headerClass: 'bg-[#f0f0f0]', 
            width: 180,
            cellRenderer: (data: any) => {
                return (
                    <p>{data.value ? moment(data.value).format(DATE_FORMAT) : '-'}</p>
                )
            }
        },
        {
            headerName: 'Duration', 
            field: 'duration', 
            headerClass: 'bg-[#f0f0f0]', 
            width: 120,
            cellRenderer: (data: any) => {
                if (!data.value) return <p>-</p>;
                const totalMinutes = parseInt(data.value);
                const hours = Math.floor(totalMinutes / 60);
                const minutes = totalMinutes % 60;
                return <p>{`${hours}:${minutes.toString().padStart(2, '0')}`}</p>;
            }
        },
        {
            headerName: 'Action',
            field: 'action',
            headerClass: 'bg-[#f0f0f0] text-center',
            width: 180,
            cellStyle: { textAlign: 'center' },
            cellRenderer: (params: any) => {
                const isAcknowledged = params.data.statusID === 2;
                const hasAcknowledgePermission = getPermissionInfo(location.pathname, "Log Activity Acknowledge", loggedinuser);
                const hasDeletePermission = getPermissionInfo(location.pathname, "Log Activity Delete", loggedinuser);
                
                return (
                    <div className='flex items-center justify-center gap-2 h-full'>
                        {isAcknowledged ? (
                            <p className='text-gray-500 text-sm'>Acknowledged</p>
                        ) : (
                            <Button 
                                theme='primary' 
                                className='!h-8 !text-sm'
                                onClick={() => handleAcknowledge(params.data)}
                                disabled={!hasAcknowledgePermission}
                            >
                                Acknowledged
                            </Button>
                        )}
                        <button
                            className='text-red-600 hover:text-red-800 disabled:text-gray-400 disabled:cursor-not-allowed'
                            onClick={() => handleDelete(params.data)}
                            disabled={!hasDeletePermission}
                            title="Delete Activity Log"
                        >
                            <Icon name="delete" size={1.2} />
                        </button>
                    </div>
                )
            }
        },
    ];

    const onGridReady = (params: any) => {
        setGridApi(params.api);
        setGridColumnApi(params.columnApi);
    };

    const handleAcknowledge = (rowData: any) => {
        // Check permission before proceeding
        if (!getPermissionInfo(location.pathname, "Activity Acknowledge", loggedinuser)) {
            toast.error("You don't have Acknowledged permission");
            return;
        }

        const activityType = rowData.typeID === 1 ? 'Observation' : rowData.typeID === 2 ? 'Activity' : 'item';
        
        confirm({
            type: 'warn',
            title: 'Acknowledge Confirmation',
            message: `Do you want to Acknowledge this ${activityType}?`,
            onConfirm: () => {
                const payload = {
                    facilityActivityLogID: rowData.facilityActivityLogID,
                    statusID: 2
                };
                
                facilityApi.updateFacilityActivityLogStatus(
                    payload,
                    (resp: any, msg: any) => {
                        toast.success(msg || 'Activity acknowledged successfully');
                        getLogActivityData();
                    },
                    (error: any) => {
                        toast.error(error);
                    }
                );
            }
        });
    };

    const handleDelete = (rowData: any) => {
        // Check permission before proceeding
        if (!getPermissionInfo(location.pathname, "delete", loggedinuser)) {
            toast.error("You don't have delete permission");
            return;
        }

        const activityType = rowData.typeID === 1 ? 'Observation' : rowData.typeID === 2 ? 'Activity' : 'item';
        
        confirm({
            type: 'delete',
            title: 'Delete Activity Log',
            message: `Are you sure you want to delete this ${activityType}?`,
            onConfirm: () => {
                const payload = {
                    FacilityActivityLogID: rowData.facilityActivityLogID
                };
                
                facilityApi.deleteFacilityActivityLog(
                    payload,
                    (resp: any, msg: any) => {
                        toast.success(msg || 'Activity log deleted successfully');
                        getLogActivityData();
                    },
                    (error: any) => {
                        toast.error(error);
                    }
                );
            }
        });
    };

    const getLogActivityData = () => {
        if (!facilityID) return;

        const payload = {
            facilityID: facilityID,
            pageNumber: currentPage,
            pageSize: PAGE_SIZE
        };

        facilityApi.getFacilityActivity(
            payload,
            (data: any) => {
                setLogActivityData(data?.facilityActivityLogList || []);
                setTotalRecords(data?.totalRecord || 0);
            },
            (error: any) => {
                toast.error(error);
                setLogActivityData([]);
            }
        );
    };

    useEffect(() => {
        if (facilityID) {
            getLogActivityData();
        }
    }, [facilityID, currentPage]);

    return (
        <div className="w-full">
            <div className='flex items-center justify-between pb-3 border-b border-gray-200'>
                <Pagination
                    perPage={PAGE_SIZE}
                    totalRecords={totalRecords}
                    onPageChange={setCurrentPage}
                    currentPage={currentPage}
                />
            </div>
            <div className="ag-theme-alpine w-full mt-4">
                <AgGridReact
                    columnDefs={columnDefs as any}
                    rowData={logActivityData || []}
                    onGridReady={onGridReady}
                    pagination={false}
                    domLayout="autoHeight"
                    loadingOverlayComponent={Loader}
                    noRowsOverlayComponent={() => (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center text-gray-500">
                                <div className="text-lg font-medium">No Log Activity Found</div>
                            </div>
                        </div>
                    )}
                />
            </div>
        </div>
    );
};

export default LogActivity;
